<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { Check } from 'lucide-svelte';

  let {
    label = '',
    value = '',
    type = 'text',
    error = '',
    placeholder = '',
    mono = false,
    onSave = async (_next: string) => {}
  }: {
    label?: string;
    value?: string;
    type?: string;
    error?: string;
    placeholder?: string;
    mono?: boolean;
    onSave?: (next: string) => Promise<void>;
  } = $props();

  const dispatch = createEventDispatcher<{ saved: string }>();

  let localValue = $state(value);
  let lastPropValue = $state(value);
  let saving = $state(false);
  let saved = $state(false);
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let savedTimer: ReturnType<typeof setTimeout> | null = null;

  // `dirty` marks "there's a local edit the caller hasn't confirmed back to
  // us yet". It is NOT the same as `saving`: `saving` flips false as soon as
  // the PATCH response resolves, but callers (e.g. UnitInspector's
  // saveNotes) then fire off a *separate*, un-awaited query invalidation —
  // the `value` prop only catches up once that background refetch lands,
  // a second network round trip. Resyncing `localValue` from `value`
  // whenever `!saving` re-ran this effect in that gap and overwrote the
  // just-saved edit with the still-stale prop, so the field visibly
  // reverted to the old text for a beat after every save. Gating on `dirty`
  // instead means we only ever adopt an external `value` change when there
  // is no unconfirmed local edit ahead of it; once the prop catches up to
  // what we saved, `dirty` clears itself.
  let dirty = false;

  $effect(() => {
    if (value !== lastPropValue) {
      lastPropValue = value;
      if (!saving) {
        localValue = value;
      }
    }
    if (value === localValue) {
      dirty = false;
    }
  });

  function scheduleSave() {
    dirty = true;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(save, 400);
  }

  async function save() {
    if (localValue === value) {
      dirty = false;
      return;
    }
    saving = true;
    error = '';
    saved = false;
    try {
      await onSave(localValue);
      dispatch('saved', localValue);
      saved = true;
      if (savedTimer) clearTimeout(savedTimer);
      savedTimer = setTimeout(() => {
        saved = false;
      }, 1500);
    } catch (e: unknown) {
      error = (e as Error)?.message || 'Save failed';
    } finally {
      saving = false;
    }
  }

  onDestroy(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (savedTimer) clearTimeout(savedTimer);
  });
</script>

<div class="space-y-1">
  {#if label}
    <label class="block text-2xs font-medium text-text-muted uppercase tracking-wide">
      {label}
    </label>
  {/if}
  <div class="relative">
    {#if type === 'textarea'}
      <textarea
        class="w-full px-2 py-1.5 text-sm border border-border-strong rounded bg-bg-canvas text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary resize-none"
        class:font-mono={mono}
        rows="3"
        {placeholder}
        bind:value={localValue}
        oninput={scheduleSave}
      ></textarea>
    {:else}
      <input
        class="w-full px-2 py-1.5 text-sm border border-border-strong rounded bg-bg-canvas text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
        class:font-mono={mono}
        {type}
        {placeholder}
        bind:value={localValue}
        oninput={scheduleSave}
      />
    {/if}
    {#if saved}
      <Check class="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-state-ok" />
    {/if}
  </div>
  {#if error}
    <p class="text-2xs text-state-error">{error}</p>
  {/if}
</div>
